import React, { useMemo, useState } from 'react';
import theme from 'themes';
import { WidgetUi } from 'common/ui';
import { ModalSize, ResourceType } from 'types';
import { LineStatusWidgetContainer, StyledModalContentContainer } from './LineStatusWidget.elements';
import { useLineStatusWidget, useLineTargets } from './hooks/useLineStatusWidget';
import { LineGuide } from 'constants/lineView';
import { toNestedData } from './utils';
import { useLineInfo } from '../../../providers/useLineInfoProvider';
import { Loader, TooltipWrapper } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  StateOverTimeCard,
  Typography,
  useDateRange,
  DateButtonWithDropdown
} from 'components';
import { ColorDataByCode } from 'common/pages/fleetV2/machine/protein/views/MachineHealth/components/MachineUtilizationWidget/utils';
import Setting from 'icons/indexed-icons/Setting';
import { useUploadLineViewImageMutation, useLineTargetsMutation } from 'api';
import { ToastMsg } from 'common/components/Toast';
import { LineImageWidget } from '../LineImageWidget/LineImageWidget';
import LineRunningStatusWidget from '../LineRunningStatusWidget/LineRunningStatusWidget';
import LineTargetModal from '../LineTargetModal/LineTargetModal';
import { calculateDaysInRange } from "common/pages/fleetV2/machine/protein/views/MachineHealth/components/MachineUtilizationWidget/utils";
import { LineStates, LineMachineStates } from 'types/protein';

interface TargetConfig {
  category?: string;
  totalHoursPerWeek?: number;
  enabled?: boolean;
  useCustomDays?: boolean;
  isVisible?: boolean;
  days?: Record<string, number>;
  averagePerDay?: number;
}

interface OnSubmitBtnParams {
  targetConfigs: Record<string, TargetConfig>;
}

export const LineStatusWidget = (): JSX.Element => {
  const { t } = useTranslation(['mh']);
  const { lineData, lineId } = useLineInfo();
  const { dateRange, setDateRange, isoDateRange } = useDateRange();
  const { startTime: startDatetime, endTime: endDatetime } = dateRange;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('running');
  const [isOpenInfoModal, setIsInfoModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const selectedNoOfDays = calculateDaysInRange({ startTime: startDatetime, endTime: endDatetime });

  // Fetch line and machine statuses
  // This will be used to display the line status and machine states in bar chart
  // and to calculate the actual running hours of the line
  const {
    lineAndMachinesTuple,
    isLoading,
    isFetching,
    error
  }: {
    lineAndMachinesTuple: [LineStates, LineMachineStates[]] | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: any;
  } = useLineStatusWidget({
    lineId: lineId as string,
    startDatetime: startDatetime instanceof Date ? startDatetime.toISOString() : startDatetime,
    endDatetime: endDatetime instanceof Date ? endDatetime.toISOString() : endDatetime
  });

  // Fetch line targets
  // This will be used to display the line targets in the modal
  // and to calculate the line running target
  const { data: lineTargetsData, refetch: lineTargetsRefetch } = useLineTargets(lineId as string);

  // Function to open the modal with the specified tab
  // This will be used to open the modal when the user clicks on the settings icon
  const openModal = (tab: string) => {
    setActiveTab(tab);
    setIsModalOpen(true);
  };

  // Function to close the modal
  // This will be used to close the modal when the user clicks on the close button
  const closeModal = () => setIsModalOpen(false);

  // Function to handle file change
  // This will be used to handle the file change when the user selects an image to upload
  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
  };

  // Function to upload the line view image
  // This will be used to upload the image when the user clicks on the apply button
  const [uploadLineViewImage] = useUploadLineViewImageMutation();

  const uploadImage = async () => {
    if (!uploadedFile) {
      console.error('No file to upload');
      return;
    }

    try {
      await uploadLineViewImage({
        assetType: ResourceType.LineImage,
        file: uploadedFile,
        lineId: lineId as string
      }).unwrap();

      ToastMsg({
        heading: 'Success',
        type: 'success',
        message: `Line image uploaded successfully`
      });
    } catch (error) {
      ToastMsg({ heading: 'Error', type: 'error', message: `Failed to upload line image` });
      console.error(error);
    }
  };


  const lineRunningTargetData = lineTargetsData?.find((item) => item?.category === 'running');

  const lineRunningTargetValue =
  lineRunningTargetData?.averageCustomHoursPerDay !== undefined && lineRunningTargetData.averageCustomHoursPerDay > 0
    ? lineRunningTargetData.averageCustomHoursPerDay
    : lineRunningTargetData?.averagePerDay !== undefined && lineRunningTargetData.averagePerDay > 0
      ? lineRunningTargetData.averagePerDay
      : undefined;

  const lineRunningTarget = Math.round((lineRunningTargetValue ?? 0) * selectedNoOfDays);

  const [lineTargets] = useLineTargetsMutation();

  // Function to handle the apply button click in the modal
  // This will be used to update the line targets when the user clicks on the apply button
  const onSubmitBtn = async ({ targetConfigs }: OnSubmitBtnParams): Promise<void> => {
    const updateTargetConfigs = Object.values(targetConfigs).map((item) => ({
      category: item?.category,
      totalHoursPerWeek: item?.totalHoursPerWeek,
      enabled: item?.enabled,
      useCustomDays: item?.useCustomDays,
      isVisible: item?.isVisible,
      days: item?.days,
      averageHoursPerDay: item?.averagePerDay
    }));

    try {
      await lineTargets({
        lineId: lineId as string,
        targetPayloadData: updateTargetConfigs
      }).unwrap();
      ToastMsg({
        heading: 'Success',
        message: 'Target set successfully',
        type: 'success',
        position: 'top-right'
      });
      lineTargetsRefetch && lineTargetsRefetch();
    } catch (error) {
      ToastMsg({
        heading: 'Error',
        message: 'Something went wrong',
        type: 'error',
        position: 'top-right'
      });
      lineTargetsRefetch && lineTargetsRefetch();
    }
  };

  // Calculate the actual running value of the line
  // This will be used to display the actual running hours of the line in the gauge chart
  const lineActualRunningValue = useMemo(() => {
    let totalRunningMilliseconds = 0;

    if(!lineAndMachinesTuple || !lineAndMachinesTuple?.[0]?.values) {
      return 0;
    }
    const values = lineAndMachinesTuple?.[0]?.values || [];
    (Array.isArray(values) ? values : []).forEach(entry => {
        if (entry?.name === "running") {
            const startTime = new Date(entry?.timestamp);
            const endTime = new Date(entry?.endTimestamp);
            totalRunningMilliseconds += (endTime.getTime() - startTime.getTime());
        }
    });

    const totalRunningHours = totalRunningMilliseconds / (1000 * 60 * 60);
    return Math.round(totalRunningHours);
  }, [lineAndMachinesTuple]);


  // Extract machines from the line and machines tuple
  // This will be used to display the machine names and running hours in the line running status widget
  const machines = useMemo(() => {
    if (!lineAndMachinesTuple) return [];
    return (
      lineAndMachinesTuple[1]?.map((machine) => ({
        name: machine.meta?.description,
        id: machine.entityId
      })) || []
    );
  }, [lineAndMachinesTuple]);

  const Main = useMemo(() => {
    if(isLoading || isFetching && !lineAndMachinesTuple)
      return <div className="widget-ui__main"><Loader size={50} /></div>;
    return (
      <div className="widget-ui__main">
        <StateOverTimeCard
          hideStateCodes
          hideSubStepIds
          nestedData={toNestedData(lineAndMachinesTuple, lineData?.name)}
          stateColors={ColorDataByCode}
          title={` `}
          isDefaultExpanded
        />
        <div className="line-status-and-image-container">
          <LineRunningStatusWidget
            machines={machines}
            lineRunningTarget={lineRunningTarget}
            selectedNoOfDays={selectedNoOfDays}
            lineActualRunningValue={lineActualRunningValue}
          />
          <LineImageWidget />
        </div>
      </div>
    );
  }, [lineData, lineAndMachinesTuple, machines, lineRunningTarget, selectedNoOfDays, lineActualRunningValue, isLoading, isFetching]);

  const widgetSettings = {
    className: 'line-status-widget',
    title: 'Line Status',
    IconRight: () => (
      <div className='widget-ui__icon-right-container'>
        <DateButtonWithDropdown {...{ dateRange, setDateRange }} />
        <div onClick={() => openModal('running')} className='widget-ui__icon-right'>
          <Setting />
        </div>

      </div>
    ),
    IconLeft: () => (
      <TooltipWrapper Tooltip="Line status definitions">
        <FontAwesomeIcon icon={faCircleInfo} onClick={() => setIsInfoModalOpen(true)} />
      </TooltipWrapper>
    ),
    hasError: error ? 'Failed to load line status' : undefined,
    Main
  };

  return (
    <LineStatusWidgetContainer>
      <WidgetUi {...widgetSettings} />
      <Modal
        visible={isOpenInfoModal}
        onClose={() => {
          setIsInfoModalOpen(false);
        }}
        title={t(LineGuide.title) as string}
        size={ModalSize.SMALL_AUTO_HEIGHT}
        maxWidth="30rem"
      >
        <StyledModalContentContainer>
          {Object.entries(LineGuide.pemeaPna.details).map(([key, detail]) => (
            <React.Fragment key={key}>
              <Typography color={theme.colors.darkGrey} size='1rem' weight="bold">
                {t(detail.title)}
              </Typography>
              <Typography
                color={theme.colors.darkGrey}
                mb='1rem'
                size={theme.typography.text.base.size}
              >
                {t(detail.text)}
              </Typography>
            </React.Fragment>
          ))}
        </StyledModalContentContainer>
      </Modal>
      <LineTargetModal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        activeTab={activeTab}
        onSubmitBtn={onSubmitBtn}
        targetData={lineTargetsData}
        handleFileChange={handleFileChange}
        uploadImage={uploadImage}
      />
    </LineStatusWidgetContainer>
  );
};
